import { memo, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import {
  maxLength,
  minValue,
  minLength,
  number,
  NumberInput,
  required,
  TextInput,
  useDataProvider,
} from "react-admin";
import parserFormatNumInput from "../../../../utils/parserFormatNumInput";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import SaleRankingList from "../../../../pages/saleRankings/SaleRankingList";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import GoodsInTransitInput from "../../common/GoodsInTransitInput";
import DraggableComponent from "../../common/DraggableComponent";
import ProductSelectionDialog from "../../common/ProductSelectionDialog";

const validateTenCode = [required(), minLength(10), maxLength(10)];
const validateQuantity = [required(), number()];
const validateDayRange = [required(), number(), minValue(1)];

const CstInput = ({
  productIdSource,
  productNameSource,
  product_name: productName,
  averageSalesSource,
  stockSource,
  buffMinSource,
  buffMaxSource,
  rMinSource,
  rMaxSource,
  bdpSource,
  finalSource,
  noteSource,
  dayRangeSource,
}) => {
  const dataProvider = useDataProvider();

  const [openSaleRanking, setOpenSaleRanking] = useState(false);
  const [openProductSelector, setOpenProductSelector] = useState(false);

  const handleCloseSaleRanking = (e, reason) => {
    if (reason !== "backdropClick") {
      setOpenSaleRanking(false);
    }
  };

  const dialogX = useMemo(() => {
    return (
      <Dialog
        open={openSaleRanking}
        onClose={handleCloseSaleRanking}
        scroll="paper"
        keepMounted={false}
        fullWidth={true}
        maxWidth="xl"
        hideBackdrop
        PaperComponent={DraggableComponent}
        aria-labelledby="draggable-dialog-title"
        sx={{
          right: "unset",
          bottom: "unset",
          // height: "100%",
          "& .MuiPaper-root": {
            m: 0,
            width: "100%",
            maxHeight: "calc(100vh - 10vh)",
          },
        }}
      >
        <Box
          id="draggable-dialog-title"
          sx={{
            cursor: "move",
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 0,
          }}
        >
          <DialogTitle>Sale Ranking</DialogTitle>
          <IconButton onClick={handleCloseSaleRanking} sx={{ mr: "11px" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          <SaleRankingList
            resource="sale-rankings"
            sources={[
              productIdSource,
              averageSalesSource,
              stockSource,
              buffMinSource,
              buffMaxSource,
              rMinSource,
          rMaxSource,
          bdpSource,
          dayRangeSource,
            ]}
            openSaleRanking={openSaleRanking}
            setOpenSaleRanking={setOpenSaleRanking}
            productNameSource={productNameSource}
          />
        </DialogContent>
      </Dialog>
    );
  }, [
    openSaleRanking,
    productIdSource,
    productNameSource,
    averageSalesSource,
    stockSource,
    buffMinSource,
    buffMaxSource,
    rMinSource,
    rMaxSource,
    bdpSource,
  ]);

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
    <Grid
      container
      spacing={2}
      sx={{
        "& .MuiGrid-item": { py: 0 },
        mt: 0,
        minWidth: "1450px",
        flexWrap: "nowrap",
      }}
    >
      <Grid item xs={12} lg={1.33}>
        <TextInput
          variant="outlined"
          source={productIdSource}
          label="mine.label.product_code"
          fullWidth
          helperText={false}
          validate={validateTenCode}
          onClick={() => setOpenProductSelector(true)}
        />
      </Grid>

      <Grid item xs={12} lg={1.8}>
        <Tooltip title={productName || ""} placement="top" arrow>
          <Box sx={{ width: "100%" }}>
            <TextInput
              variant="outlined"
              source={productNameSource}
              label="Nama Produk"
              disabled
              fullWidth
              helperText={false}
            />
          </Box>
        </Tooltip>
      </Grid>

      <Grid item xs={12} lg={1.33}>
        <TextInput
          variant="outlined"
          source={averageSalesSource}
          label="mine.label.average_sales"
          disabled
          fullWidth
          helperText={false}
          {...parserFormatNumInput}
        />
      </Grid>

      <Grid item xs={12} lg={1}>
        <TextInput
          variant="outlined"
          source={buffMinSource}
          label="Buff Min"
          disabled
          fullWidth
          helperText={false}
          {...parserFormatNumInput}
        />
      </Grid>

      <Grid item xs={12} lg={1}>
        <TextInput
          variant="outlined"
          source={buffMaxSource}
          label="Buff Max"
          disabled
          fullWidth
          helperText={false}
          {...parserFormatNumInput}
        />
      </Grid>

      <Grid item xs={12} lg={1}>
        <TextInput
          variant="outlined"
          source={stockSource}
          label="mine.label.stock"
          disabled
          fullWidth
          helperText={false}
          {...parserFormatNumInput}
        />
      </Grid>

      <Grid item xs={12} lg={1}>
        <TextInput
          variant="outlined"
          source={rMinSource}
          label="R Min"
          disabled
          fullWidth
          helperText={false}
          {...parserFormatNumInput}
        />
      </Grid>

      <Grid item xs={12} lg={1}>
        <TextInput
          variant="outlined"
          source={rMaxSource}
          label="R Max"
          disabled
          fullWidth
          helperText={false}
          {...parserFormatNumInput}
        />
      </Grid>

      <Grid item xs={12} lg={0.99}>
        <GoodsInTransitInput
          variant="outlined"
          source={bdpSource}
          dependOn={productIdSource}
          label="BDP"
          disabled
          fullWidth
          helperText={false}
          {...parserFormatNumInput}
        />
      </Grid>

      <Grid item xs={12} lg={1}>
        <TextInput
          variant="outlined"
          source={finalSource}
          label="Final"
          fullWidth
          validate={validateQuantity}
          helperText={false}
          format={(val) => {
            return +val;
          }}
          {...parserFormatNumInput}
        />
      </Grid>

      <Grid item xs={12} lg={2.32}>
        <TextInput
          variant="outlined"
          source={noteSource}
          label="mine.label.note"
          fullWidth
          helperText={false}
        />
        <NumberInput
          variant="outlined"
          source={dayRangeSource}
          label={false}
          validate={validateDayRange}
          fullWidth
          helperText={false}
          defaultValue={0}
          readOnly
          sx={{ display: "none" }}
        />
      </Grid>
      {/*  SALE RANKING DIALOG */}
      {dialogX}
      <ProductSelectionDialog
        open={openProductSelector}
        onClose={() => setOpenProductSelector(false)}
        onOpenSaleRanking={() => setOpenSaleRanking(true)}
        productIdSource={productIdSource}
        productNameSource={productNameSource}
        fieldSources={{
          averageSales: averageSalesSource,
          stock: stockSource,
          bufferMinimal: buffMinSource,
          bufferMaximal: buffMaxSource,
          restockMinimal: rMinSource,
          restockMaximal: rMaxSource,
          dayRange: dayRangeSource,
        }}
      />
      {/*  SALE RANKING DIALOG */}
    </Grid>
    </Box>
  );
};

const propsAreEqual = (prevProps, nextProps) => {
  if (
    prevProps.ten_code === nextProps.ten_code &&
    prevProps.final === nextProps.final &&
    prevProps.note === nextProps.note
  ) {
    // tidak perlu render ulang
    return true;
  }
  // perlu dirender ulang
  return false;
};
const Dudupap = memo(CstInput, propsAreEqual);

const Inputs = ({
  formData, // The whole form data
  scopedFormData, // The data for this item of the ArrayInput
  getSource, // A function to get the valid source inside an ArrayInput
  ...rest
}) => {
  return (
    <Dudupap
      {...scopedFormData}
      productIdSource={getSource("ten_code")}
      productNameSource={getSource("product_name")}
      averageSalesSource={getSource("average_sales")}
      stockSource={getSource("stock")}
      buffMinSource={getSource("buffer_minimal")}
      buffMaxSource={getSource("buffer_maximal")}
      rMinSource={getSource("restock_minimal")}
      rMaxSource={getSource("restock_maximal")}
      bdpSource={getSource("goods_in_transit")}
      finalSource={getSource("final")}
      noteSource={getSource("note")}
      dayRangeSource={getSource("day_range")}
    />
  );
};
export default Inputs;
